import React from 'react';
import { cn } from '@/lib/utils';
import { useSourcesStore } from '@/stores';
import { useQueryFiltersStore } from '@/stores/query-filters.store';
import { DateTimeMode, type QueryFilters } from '@/utils/query-filters';
import { SourcesFilterChip } from './SourcesFilterChip';
import { DateTimeFilterChip } from './DateTimeFilterChip';
import { ConfidenceFilterChip } from './ConfidenceFilterChip';

interface DraftOverride {
    sources: {
        selected: Set<string>;
        excluded: Set<string>;
        all: boolean;
    };
    filters: QueryFilters;
    queryPanelAllSourcesLabel?: string;
    sourcesPopover: {
        open: boolean;
        onOpenChange: (open: boolean) => void;
        content: React.ReactNode;
    };
    sourcesInvalid?: boolean;
}

interface FilterBarProps {
    onDateTimeFilterApply?: (range: {
        startTime: Date;
        endTime: Date;
        mode: DateTimeMode;
    }) => void;
    onConfidenceFilterApply?: (range: { min: number; max: number }) => void;
    sourceIds?: string[];
    onRemoveSource?: (id: string) => void;
    draftOverride?: DraftOverride;
    tagVariant?: 'base' | 'plain';
    className?: string;
}

export function FilterBar({
    onDateTimeFilterApply,
    onConfidenceFilterApply,
    sourceIds: explicitSourceIds,
    onRemoveSource,
    draftOverride,
    tagVariant = 'base',
    className,
}: FilterBarProps) {
    const sourcesStore = useSourcesStore();
    const filtersStore = useQueryFiltersStore();

    const sources = draftOverride?.sources ?? {
        selected: sourcesStore.selected,
        excluded: sourcesStore.excluded,
        all: sourcesStore.all,
    };
    const filters = draftOverride?.filters ?? filtersStore.filters;

    const hasSourceFilter = explicitSourceIds
        ? explicitSourceIds.length > 0
        : draftOverride
          ? true
          : sources.all
            ? sources.excluded.size > 0
            : sources.selected.size > 0;
    const hasDateTimeFilter = filters.dateTime.mode !== DateTimeMode.ALL;
    const hasConfidenceFilter = filters.confidence.enabled;

    if (!hasSourceFilter && !hasDateTimeFilter && !hasConfidenceFilter) {
        return null;
    }

    return (
        <div className={cn('flex flex-wrap items-center gap-3', className)}>
            {hasSourceFilter && (
                <SourcesFilterChip
                    tagVariant={tagVariant}
                    sourceIds={explicitSourceIds}
                    onRemoveSource={onRemoveSource}
                    selection={draftOverride ? sources : undefined}
                    popover={draftOverride?.sourcesPopover}
                    emptyLabel={draftOverride?.queryPanelAllSourcesLabel}
                    invalidHighlight={draftOverride?.sourcesInvalid}
                />
            )}
            {hasDateTimeFilter && onDateTimeFilterApply && (
                <DateTimeFilterChip
                    tagVariant={tagVariant}
                    onApply={onDateTimeFilterApply}
                    dateTimeOverride={
                        draftOverride ? filters.dateTime : undefined
                    }
                />
            )}
            {hasConfidenceFilter && onConfidenceFilterApply && (
                <ConfidenceFilterChip
                    tagVariant={tagVariant}
                    onApply={onConfidenceFilterApply}
                    confidence={filters.confidence}
                />
            )}
        </div>
    );
}
